/**
 * Module ở cổng tenant (ADR-0096 mục 8): danh sách module đã duyệt, gói chính người dùng đã nộp, nộp gói, cài và theo
 * dõi lượt cài. Nhóm route tenant của ADR-0007, `tenant_id` và người nộp lấy từ token.
 */
import { dungApiThat, goiApi } from '../api/http';
import { mockRequest } from '../../mock/transport';
import {
  caiGia,
  choDuyetGia,
  duyetToChucGia,
  goGia,
  goiCuaToiGia,
  luotCaiGia,
  moduleDaDuyetGia,
  nopGoiToChucGia,
  tuChoiToChucGia,
  xoaModuleGia,
} from '../../mock/module-to-chuc';
import type { ModulePackageResult, ModuleVersion, ModuleVersionPage } from '../../modules/quan-ly-module/types';
import type { LuotCai, ModuleDaDuyet, ModuleDaDuyetPage } from '../../modules/module-to-chuc/types';

// `Limit` dùng chung của endpoint phân trang: tối đa 100 (openapi/platform-api.yaml).
const LIMIT_TOI_DA = 100;

async function layHetTrang<T>(duongDan: string): Promise<T[]> {
  const tatCa: T[] = [];
  let cursor: string | undefined;
  for (;;) {
    const trang = await goiApi<{ items: T[]; next_cursor: string | null }>(duongDan, {
      query: { limit: LIMIT_TOI_DA, cursor },
    });
    tatCa.push(...trang.items);
    if (!trang.next_cursor) return tatCa;
    cursor = trang.next_cursor;
  }
}

export const moduleToChucRepository = {
  /** `GET /api/v1/modules`: bản đang PUBLISHED của mọi module. */
  daDuyet: (): Promise<ModuleDaDuyet[]> =>
    dungApiThat() ? layHetTrang<ModuleDaDuyetPage['items'][number]>('/api/v1/modules') : mockRequest(moduleDaDuyetGia(), []),

  /** `GET /api/v1/modules/submissions`: phiên bản chính người đăng nhập đã nộp. */
  goiCuaToi: (): Promise<ModuleVersion[]> =>
    dungApiThat()
      ? layHetTrang<ModuleVersionPage['items'][number]>('/api/v1/modules/submissions')
      : mockRequest(goiCuaToiGia(), []),

  /**
   * `GET /api/v1/modules/reviews` (chỉ quản trị tổ chức, ADR-0115): mọi phiên bản của module riêng tổ chức, mọi người
   * nộp, mọi trạng thái, mới nộp trước. Vừa là hàng chờ duyệt vừa là lịch sử cập nhật phiên bản.
   */
  choDuyet: (): Promise<ModuleVersion[]> =>
    dungApiThat()
      ? layHetTrang<ModuleVersionPage['items'][number]>('/api/v1/modules/reviews')
      : mockRequest(choDuyetGia(), []),

  /**
   * `POST /api/v1/modules/reviews/{code}/versions/{version}/approve`. `body: {}` chứ không `undefined`: server đòi
   * Content-Type dù ghi chú tuỳ chọn, thiếu thì trả 415 (như `quanLyModuleRepository.duyet`).
   */
  duyet: (code: string, version: string, note?: string): Promise<ModuleVersion> =>
    dungApiThat()
      ? goiApi<ModuleVersion>(
          `/api/v1/modules/reviews/${encodeURIComponent(code)}/versions/${encodeURIComponent(version)}/approve`,
          { method: 'POST', body: note ? { note } : {} },
        )
      : mockRequest(undefined).then(() => duyetToChucGia(code, version)),

  /** `POST /api/v1/modules/reviews/{code}/versions/{version}/reject`: lý do bắt buộc, người nộp đọc để sửa. */
  tuChoi: (code: string, version: string, reason: string): Promise<ModuleVersion> =>
    dungApiThat()
      ? goiApi<ModuleVersion>(
          `/api/v1/modules/reviews/${encodeURIComponent(code)}/versions/${encodeURIComponent(version)}/reject`,
          { method: 'POST', body: { reason } },
        )
      : mockRequest(undefined).then(() => tuChoiToChucGia(code, version, reason)),

  /** `POST /api/v1/modules/submissions`: không đòi chữ ký; gói nằm IN_REVIEW chờ quản trị tổ chức duyệt (ADR-0115). */
  nopGoi: (tep: File, khoaLap: string): Promise<ModulePackageResult> =>
    dungApiThat()
      ? goiApi<ModulePackageResult>('/api/v1/modules/submissions', {
          method: 'POST',
          idempotencyKey: khoaLap,
          bieuMau: (() => {
            const fd = new FormData();
            fd.append('file', tep);
            return fd;
          })(),
        })
      : mockRequest(undefined).then(() => nopGoiToChucGia(tep)),

  /**
   * `DELETE /api/v1/modules/submissions/{code}` (ADR-0112): người nộp xoá hẳn module khỏi Marketplace, trả 204.
   * Còn tổ chức đang cài thì 409, câu lỗi của server nói cách xử lý.
   */
  xoaModule: (code: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(`/api/v1/modules/submissions/${encodeURIComponent(code)}`, { method: 'DELETE' })
      : mockRequest(undefined).then(() => xoaModuleGia(code)),

  /** `POST /api/v1/modules/{code}/install` (Tenant Admin, ADR-0048): bản đang PUBLISHED, trả 202 INSTALLING. */
  cai: (code: string, khoaLap: string): Promise<LuotCai> =>
    dungApiThat()
      ? goiApi<LuotCai>(`/api/v1/modules/${encodeURIComponent(code)}/install`, {
          method: 'POST',
          idempotencyKey: khoaLap,
          body: {},
        })
      : mockRequest(undefined).then(() => caiGia(code)),

  /**
   * `POST /api/v1/modules/{code}/uninstall` với thân `{}`: gỡ mà giữ database của module ở ARCHIVED (ADR-0049 mục 3).
   * Trả 202 REMOVING.
   *
   * `body: {}` chứ không bỏ trống: generated `ModulesApi` đòi `Content-Type` hợp lệ dù `requestBody` là optional —
   * thiếu header thì Spring trả `415` ("Kiểu nội dung null không được hỗ trợ") trước khi vào tới nghiệp vụ.
   */
  go: (code: string, khoaLap: string): Promise<LuotCai> =>
    dungApiThat()
      ? goiApi<LuotCai>(`/api/v1/modules/${encodeURIComponent(code)}/uninstall`, {
          method: 'POST',
          idempotencyKey: khoaLap,
          body: {},
        })
      : mockRequest(undefined).then(() => goGia(code)),

  /** `GET /api/v1/modules/{code}/installation` (Tenant Admin). */
  luotCai: (code: string): Promise<LuotCai> =>
    dungApiThat()
      ? goiApi<LuotCai>(`/api/v1/modules/${encodeURIComponent(code)}/installation`)
      : mockRequest(undefined).then(() => luotCaiGia(code)),
};
